import type { ReactNode } from 'react';
import {
  AuditAiSystemDetails,
  isRepoSyncLogEntry,
  LogEntry,
  RepoSyncDetails,
} from '../../../types/SystemLogging.types';
import { formatEventDate } from '../../../utils/EventFormatting.util';
import { getUserInitials } from '../../../utils/Initials.util';

type SideDrawerItemProps = {
  children: ReactNode;
  label: string;
  className?: string;
};

function SideDrawerItem({
  children,
  label,
  className = 'flex flex-col gap-2',
}: SideDrawerItemProps) {
  return (
    <div className={className}>
      <div className="text-xs leading-5 text-gray-550 font-medium uppercase">
        {label}:
      </div>
      {children}
    </div>
  );
}

const repoSyncSeverityStyles: Record<RepoSyncDetails['severity'], string> = {
  Low: 'bg-cyan-100 text-cyan-700',
  Moderate: 'bg-yellow-100 text-yellow-700',
  High: 'bg-rose-100 text-rose-700',
};

function renderAuditAiSystemDetails(details: AuditAiSystemDetails): ReactNode[] {
  return [
    <SideDrawerItem key="description" label="description">
      <p className="w-full whitespace-normal break-words text-sm text-zinc-900">
        {details.description}
      </p>
    </SideDrawerItem>,
  ];
}

function renderRepoSyncDetails(details: RepoSyncDetails): ReactNode[] {
  return [
    <SideDrawerItem key="severity" label="severity">
      <span
        className={`inline-flex w-min items-center px-2.5 py-0.5 rounded-[10px] text-xs font-medium text-nowrap ${
          repoSyncSeverityStyles[details.severity]
        }`}
      >
        {details.severity}
      </span>
    </SideDrawerItem>,

    <SideDrawerItem key="description" label="description">
      <p className="w-full whitespace-normal break-words text-sm text-zinc-900">
        {details.description}
      </p>
    </SideDrawerItem>,

    <div key="repo-info" className="flex flex-col gap-4">
      <SideDrawerItem label="repository">
        <p className="text-sm text-zinc-900 mb-2">
          {details.repository ?? '—'}
        </p>
        <div className="flex gap-2">
          <SideDrawerItem label="files" className="flex flex-col gap-2 w-1/2">
            <p className="text-sm text-gray-550">
              +{details.filesAdded} / ~{details.filesModified} / -
              {details.filesDeleted}
            </p>
          </SideDrawerItem>
          <SideDrawerItem
            label="commit"
            className="flex flex-col gap-2 w-1/2 justify-between"
          >
            <p className="text-sm text-gray-550">{details.commit ?? '—'}</p>
          </SideDrawerItem>
        </div>
      </SideDrawerItem>
    </div>,

    ...(details.uiMessage
      ? [
          <SideDrawerItem key="compliance-impact" label="compliance impact">
            <p className="w-full whitespace-normal break-words text-sm text-zinc-900">
              {details.uiMessage}
            </p>
          </SideDrawerItem>,
        ]
      : []),

    ...(details.annexIVSections?.length
      ? [
          <SideDrawerItem key="annex-iv" label="annex iv sections">
            <p className="text-sm text-gray-550">
              {details.annexIVSections.join(', ')}
            </p>
          </SideDrawerItem>,
        ]
      : []),
  ];
}

export function getAuditLogDrawerContent(entry: LogEntry): ReactNode[] {
  const initials = getUserInitials(entry.user);

  return [
    <div key="who-when" className="flex gap-2">
      <SideDrawerItem label="user" className="flex flex-col gap-2 w-1/2">
        <div className="flex gap-2 items-center">
          <span className="w-6 h-6 rounded-xl bg-[#EEEEF0] flex items-center justify-center">
            <span className="font-inter text-center font-normal text-xs leading-3 text-[#4E4E56]">
              {initials}
            </span>
          </span>
          <span className="text-left text-sm text-gray-550">{entry.user}</span>
        </div>
      </SideDrawerItem>
      <SideDrawerItem
        label="date"
        className="flex flex-col gap-2 w-1/2 justify-between"
      >
        <p className="text-sm text-gray-550">{formatEventDate(entry.date)}</p>
      </SideDrawerItem>
    </div>,

    ...(entry.aiSystem
      ? [
          <SideDrawerItem key="ai-system" label="ai system">
            <p className="text-sm text-zinc-900">{entry.aiSystem}</p>
          </SideDrawerItem>,
        ]
      : []),

    ...(entry.details
      ? isRepoSyncLogEntry(entry)
        ? renderRepoSyncDetails(entry.details as RepoSyncDetails)
        : renderAuditAiSystemDetails(entry.details as AuditAiSystemDetails)
      : []),
  ];
}
